/**
 * PetClean Theme — theme.js
 * Vanilla JS only. No dependencies.
 */

(function () {
  'use strict';

  /* ── Sticky Header ───────────────────────────────────────── */
  const header = document.querySelector('.site-header');
  if (header) {
    const onScroll = () => {
      header.classList.toggle('is-scrolled', window.scrollY > 60);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ── Mobile Nav Drawer ───────────────────────────────────── */
  const menuBtn  = document.getElementById('header-menu-btn');
  const drawer   = document.getElementById('nav-drawer');
  const overlay  = document.getElementById('nav-overlay');
  const closeBtn = document.getElementById('nav-close');

  const openNav = () => {
    drawer?.classList.add('is-open');
    overlay?.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    drawer?.setAttribute('aria-hidden', 'false');
    closeBtn?.focus();
  };
  const closeNav = () => {
    drawer?.classList.remove('is-open');
    overlay?.classList.remove('is-open');
    document.body.style.overflow = '';
    drawer?.setAttribute('aria-hidden', 'true');
    menuBtn?.focus();
  };

  menuBtn?.addEventListener('click', openNav);
  closeBtn?.addEventListener('click', closeNav);
  overlay?.addEventListener('click', closeNav);
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && drawer?.classList.contains('is-open')) closeNav();
  });

  /* ── Before & After Sliders ──────────────────────────────── */
  document.querySelectorAll('.ba-slider').forEach((slider) => {
    const afterEl = slider.querySelector('.ba-slider__after');
    const handle  = slider.querySelector('.ba-slider__handle');
    if (!afterEl || !handle) return;

    let isDragging = false;
    let currentPct = 50;

    const clamp = (v, min, max) => Math.min(Math.max(v, min), max);

    const getClientX = (e) =>
      e.touches ? e.touches[0].clientX : e.clientX;

    const update = (pct) => {
      currentPct = clamp(pct, 0, 100);
      afterEl.style.clipPath = `inset(0 ${100 - currentPct}% 0 0)`;
      handle.style.left = `${currentPct}%`;
    };

    const startDrag = (e) => {
      isDragging = true;
      const rect = slider.getBoundingClientRect();
      update(((getClientX(e) - rect.left) / rect.width) * 100);
    };

    const onDrag = (e) => {
      if (!isDragging) return;
      const rect = slider.getBoundingClientRect();
      update(((getClientX(e) - rect.left) / rect.width) * 100);
    };

    const endDrag = () => { isDragging = false; };

    slider.addEventListener('mousedown',  startDrag);
    slider.addEventListener('touchstart', startDrag, { passive: true });
    window.addEventListener('mousemove',  onDrag);
    window.addEventListener('touchmove',  onDrag, { passive: true });
    window.addEventListener('mouseup',    endDrag);
    window.addEventListener('touchend',   endDrag);

    // Keyboard support for handle
    handle.setAttribute('tabindex', '0');
    handle.setAttribute('role', 'slider');
    handle.setAttribute('aria-label', 'Before and after comparison slider');
    handle.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft')  update(currentPct - 5);
      if (e.key === 'ArrowRight') update(currentPct + 5);
    });

    update(50);
  });

  /* ── FAQ Accordion ───────────────────────────────────────── */
  document.querySelectorAll('.faq-item__question').forEach((btn) => {
    btn.addEventListener('click', () => {
      const item   = btn.closest('.faq-item');
      const isOpen = item.classList.contains('is-open');
      // Close all
      document.querySelectorAll('.faq-item').forEach((i) => {
        i.classList.remove('is-open');
        i.querySelector('.faq-item__question').setAttribute('aria-expanded', 'false');
      });
      // Toggle current
      if (!isOpen) {
        item.classList.add('is-open');
        btn.setAttribute('aria-expanded', 'true');
      }
    });
    btn.setAttribute('aria-expanded', 'false');
  });

  /* ── Testimonials Carousel ───────────────────────────────── */
  const carousel = document.querySelector('.testimonials__carousel');
  if (carousel) {
    const track    = carousel.querySelector('.testimonials__track');
    const dots     = carousel.querySelectorAll('.testimonials__dot');
    const cards    = track?.querySelectorAll('.testimonial-card');
    let current    = 0;
    let autoTimer  = null;

    const perView = () => {
      if (window.innerWidth < 768)  return 1;
      if (window.innerWidth < 1024) return 2;
      return 3;
    };

    const maxIndex = () => Math.max(0, cards.length - perView());

    const goTo = (idx) => {
      if (!track || !cards.length) return;
      current = Math.min(Math.max(idx, 0), maxIndex());
      const cardW = cards[0].offsetWidth + 24; // 24 = gap
      track.style.transform = `translateX(-${current * cardW}px)`;
      dots.forEach((d, i) => d.classList.toggle('is-active', i === current));
    };

    dots.forEach((dot, i) => dot.addEventListener('click', () => { goTo(i); resetAuto(); }));

    const startAuto = () => {
      autoTimer = setInterval(() => {
        goTo(current >= maxIndex() ? 0 : current + 1);
      }, 4500);
    };
    const resetAuto = () => { clearInterval(autoTimer); startAuto(); };

    startAuto();
    carousel.addEventListener('mouseenter', () => clearInterval(autoTimer));
    carousel.addEventListener('mouseleave', startAuto);

    // Touch/swipe
    let touchStartX = 0;
    track?.addEventListener('touchstart', (e) => { touchStartX = e.touches[0].clientX; }, { passive: true });
    track?.addEventListener('touchend', (e) => {
      const diff = touchStartX - e.changedTouches[0].clientX;
      if (Math.abs(diff) > 50) {
        goTo(diff > 0 ? current + 1 : current - 1);
        resetAuto();
      }
    });

    if (dots.length > 0) dots[0].classList.add('is-active');
    window.addEventListener('resize', () => goTo(current));
  }

  /* ── Scroll Animations ───────────────────────────────────── */
  if ('IntersectionObserver' in window) {
    const animObs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            animObs.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -48px 0px' }
    );
    document.querySelectorAll('.anim-on-scroll').forEach((el) => animObs.observe(el));
  } else {
    // Fallback: show all
    document.querySelectorAll('.anim-on-scroll').forEach((el) => el.classList.add('is-visible'));
  }

  /* ── Cart Count ──────────────────────────────────────────── */
  const updateCart = () => {
    fetch('/cart.js', { headers: { 'Content-Type': 'application/json' } })
      .then((r) => r.json())
      .then((cart) => {
        document.querySelectorAll('.header__cart-count').forEach((el) => {
          el.textContent = cart.item_count;
          el.style.display = cart.item_count > 0 ? 'flex' : 'none';
        });
      })
      .catch(() => {});
  };
  updateCart();

  /* ── Add to Cart ─────────────────────────────────────────── */
  document.querySelectorAll('[data-atc]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const variantId = btn.dataset.variantId;
      if (!variantId) return;
      const originalText = btn.textContent;
      btn.textContent = 'Adding…';
      btn.disabled = true;
      try {
        const res = await fetch(window.theme?.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1 }),
        });
        if (res.ok) {
          btn.textContent = 'Added ✓';
          updateCart();
          setTimeout(() => { btn.textContent = originalText; btn.disabled = false; }, 2000);
        } else {
          throw new Error();
        }
      } catch {
        btn.textContent = 'Error — try again';
        setTimeout(() => { btn.textContent = originalText; btn.disabled = false; }, 2000);
      }
    });
  });

  /* ── Email Capture Form ──────────────────────────────────── */
  const emailForm = document.querySelector('.email-capture__form');
  if (emailForm) {
    emailForm.addEventListener('submit', (e) => {
      e.preventDefault();
      const input   = emailForm.querySelector('input[type="email"]');
      const success = document.querySelector('.email-capture__success');
      if (!input?.value) return;
      // Integrate with Shopify customer/newsletter endpoint as needed
      if (success) { success.style.display = 'block'; emailForm.style.display = 'none'; }
    });
  }

  /* ── Announcement Bar Scroll ─────────────────────────────── */
  const annBar = document.querySelector('.announcement-bar');
  if (annBar) {
    const msgs = annBar.querySelectorAll('.announcement-bar__message');
    if (msgs.length > 1) {
      let annIdx = 0;
      // Already visible via CSS; cycle for mobile single-message view
      setInterval(() => {
        annIdx = (annIdx + 1) % msgs.length;
      }, 4000);
    }
  }

  /* ── Video Pause Toggle ──────────────────────────────────── */
  document.querySelectorAll('.video-feature__pause-btn').forEach((btn) => {
    const video = btn.closest('.video-feature__media')?.querySelector('video');
    if (!video) return;
    btn.addEventListener('click', () => {
      if (video.paused) { video.play(); btn.innerHTML = '⏸'; }
      else              { video.pause(); btn.innerHTML = '▶'; }
    });
  });

  /* ── Lazy Image Loading (native fallback) ────────────────── */
  document.querySelectorAll('img[data-src]').forEach((img) => {
    if ('loading' in HTMLImageElement.prototype) {
      img.src = img.dataset.src;
      img.loading = 'lazy';
    } else {
      const imgObs = new IntersectionObserver((entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.src = entry.target.dataset.src;
            obs.unobserve(entry.target);
          }
        });
      });
      imgObs.observe(img);
    }
  });

})();
